"use client"

import { CardBasic } from "@/components/ui-library-new/card/CardBasic"
import { IconCircle } from "@/components/ui-library-new/icon/IconCircle"
import { Button } from "@/components/ui-library/button/Button"
import { trackEvent } from "@/lib/amplitude/amplitude"
import { VACCINATION_CTA_EVENTS } from "@/lib/amplitude/vaccinationCtaEvents"
import { urlParser } from "@/lib/urlParser"
import { ServiceTypeStoryblok } from "@/types/component-types"
import { cn } from "@/utils/utils"
import { numericPrice } from "@/utils/vaccineLabels"
import Link from "next/link"
import { usePathname } from "next/navigation"

export type ServiceStory = ServiceTypeStoryblok

const CardPrice = ({ value, discount }: { value: string | undefined; discount?: string }) => {
  const price = numericPrice(value)

  // A range like "490-1150" is a price, just not one to do arithmetic on. Shown as written, and no
  // discount is struck from it — there is no single number to subtract from.
  if (price === null) return <>{value?.trim() ? `${value} kr` : "Gratis"}</>
  if (price <= 0) return <>Gratis</>

  const amount = numericPrice(discount)
  if (!amount) return <>{`${price} kr`}</>

  return (
    <>
      <span className="text-neutral-30 line-through">{`${price} kr`}</span>{" "}
      <span>{`${Math.max(price - amount, 0)} kr`}</span>
    </>
  )
}

// Both card actions are styled identically — one source so they can't drift apart.
const actionButtonClasses =
  "py-1 text-sm font-semibold tracking-[-0.045em] 2xl:px-6 2xl:py-2 2xl:text-[17px] 2xl:leading-[23px]"

export const ServicePriceCard = ({ blok, showBookAction = true }: { blok: ServiceStory; showBookAction?: boolean }) => {
  const pathname = usePathname()
  const { name, icon, description, price_adult, price_child, price_member, adult_only, read_more_link, book_link } =
    blok
  return (
    <CardBasic className="min-h-[172px] justify-center p-4 2xl:min-h-[280px] 2xl:justify-between 2xl:p-8">
      <div className="mb-3 flex items-center gap-3 py-0 text-sm font-semibold 2xl:mb-2 2xl:gap-4 2xl:py-2">
        {/* Icon */}
        <IconCircle
          icon={icon}
          className="h-10 w-10 lg:h-14 lg:w-14 2xl:h-[70px] 2xl:w-[70px]"
          iconClassName="h-5 w-5 lg:h-7 lg:w-7 2xl:h-8 2xl:w-8"
        />

        {/* Name & chips */}
        <div className="flex min-w-0 flex-col items-start gap-1 2xl:gap-2">
          <p className="text-[15px] font-semibold text-neutral-90 2xl:text-xl">{name}</p>
        </div>
      </div>
      {/* description */}
      <p className="mb-6 text-sm tracking-normal text-neutral-50 2xl:mb-10 2xl:text-base">{description}</p>
      {/* row for prices, and read more button and book time button*/}
      <div
        className={cn("flex items-center justify-between gap-4 tracking-normal", {
          "items-end": adult_only
        })}
      >
        <div className="flex shrink-0 flex-col">
          {Number(price_adult) === 0 && Number(price_child) === 0 ? (
            <span className="whitespace-nowrap text-lg font-bold leading-tight text-neutral-90 2xl:text-2xl">
              Gratis
            </span>
          ) : (
            <>
              <span className="whitespace-nowrap text-lg font-bold leading-tight text-neutral-90 2xl:text-2xl">
                <CardPrice value={price_adult} />
              </span>
              {Boolean(price_member) && (
                <span className="whitespace-nowrap text-xs font-normal tracking-wide text-neutral-90 2xl:text-[14px]">
                  Apoteket AB medlem: <CardPrice value={String(price_member)} />
                </span>
              )}
              {!adult_only && (
                <span className="whitespace-nowrap text-xs font-normal tracking-wide text-neutral-90 2xl:text-[14px]">
                  Barn: <CardPrice value={price_child} discount={undefined} />
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {read_more_link?.cached_url && (
            <Button asChild size="small" variant="neutral" className={actionButtonClasses}>
              <Link href={urlParser(read_more_link.cached_url)}>Läs mer</Link>
            </Button>
          )}
          {showBookAction && book_link?.cached_url && (
            <Button asChild size="small" variant="primary" className={actionButtonClasses}>
              <Link
                onClick={() =>
                  trackEvent(VACCINATION_CTA_EVENTS.vaccine_card_book_cta_pressed, { page_path: pathname })
                }
                href={urlParser(book_link.cached_url)}
              >
                Boka tid
              </Link>
            </Button>
          )}
        </div>
      </div>
    </CardBasic>
  )
}
